<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { storeToRefs } from 'pinia';
import MediaImage from '@components/MediaImage.vue';
import Modal from '@components/Modal.vue';
import { useGalleryStore } from '@stores/useGalleryStore.ts';
import ContactOfferButton from '@components/ContactOfferButton.vue';
import { trans } from '@elements/translation';
import Typography from '@components/Typography.vue';
import Icon from '@components/Icon.vue';
import type { UUIDv4 } from '@global-types/uuid.ts';
import { UserAuth } from '@tools/user/auth';
import IconCaretLeft from '@icons/ph_caret-left.svg';
import IconCaretRight from '@icons/ph_caret-right.svg';
import IconChatsFill from '@icons/ph_chats-fill.svg';

interface GalleryProps {
  isOpen?: boolean,
  mediaUuids: UUIDv4[],
  hashId?: string | null,
  hasCtaLayer?: boolean,
  sellerOrganizationId: number | null,
  proposalId?: number,
}

const props = withDefaults(defineProps<GalleryProps>(), {
  isOpen: false,
  hashId: null,
  hasCtaLayer: false,
});

const emit = defineEmits([ 'close' ]);

const galleryStore = useGalleryStore();

const {
  setCurrentIndex,
  isNextIndex,
  makeUnscalable,
  makeScalable,
} = galleryStore;

const { currentIndex } = storeToRefs(galleryStore);
const ctaLayer = ref<boolean>(false);

const total = computed<number>(() => props.mediaUuids.length);
const showCtaLayer = computed<boolean>(() => props.hasCtaLayer && ctaLayer.value);

const ratio = (index: number): number[] | undefined => (index === 0 ? [ 3, 2 ] : undefined);

const previous = (): void => {
  if (ctaLayer.value) {
    ctaLayer.value = false;
    return;
  }

  const previousIndex = (currentIndex.value - 1);

  setCurrentIndex(previousIndex >= 0 ? previousIndex : (total.value + previousIndex));

  if (props.hasCtaLayer && currentIndex.value === total.value - 1) {
    ctaLayer.value = true;
  }
};

const next = (): void => {
  if (props.hasCtaLayer && currentIndex.value === total.value - 1 && !ctaLayer.value) {
    ctaLayer.value = true;
    return;
  }

  ctaLayer.value = false;

  setCurrentIndex((currentIndex.value + 1) % total.value);
};

const close = (): void => {
  makeUnscalable();

  setCurrentIndex(0);

  emit('close');
};

const handleKeydown = (event: KeyboardEvent): void => {
  if (props.isOpen) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const actions: Record<string, () => void> = {
      ArrowLeft: previous,
      ArrowRight: next,
      Escape: close,
    };

    actions[event.key]?.();
  }
};

onMounted(() => {
  makeScalable();
  // Third argument to avoid flickity keydown event
  document.addEventListener('keydown', handleKeydown, true);
});

onUnmounted(() => {
  makeUnscalable();
  document.removeEventListener('keydown', handleKeydown, true);
});

watch(() => props.isOpen, (open) => {
  // eslint-disable-next-line no-unused-expressions
  open ? makeScalable() : makeUnscalable();
});

const bodyKey = computed<string>(() => `${props.hashId}_${props.isOpen ? '1' : '0'}`);

const contactHashIds = computed<string[]>(() => (props.hashId ? [ props.hashId ] : []));

const isOwner = computed<boolean>(() => UserAuth.getUser()?.organization.id === props.sellerOrganizationId);
</script>

<template>
  <Modal
    v-if="isOpen"
    :is-opened="isOpen"
    :on-close="close"
    class="gallery-modal"
  >
    <template #header>
      <span class="gallery-modal__header">
        {{ trans('global.photo') }} {{ currentIndex + 1 }} / {{ total }}
      </span>
    </template>

    <template #body>
      <div class="gallery-modal__body-wrapper">
        <div
          :key="bodyKey"
          v-touch:swipe.left="next"
          v-touch:swipe.right="previous"
          class="gallery-modal__body"
        >
          <div class="gallery-modal__pictures">
            <MediaImage
              v-for="(uuid, index) in mediaUuids"
              v-show="index === currentIndex"
              :key="uuid"
              :img-class="`gallery-modal__img ${showCtaLayer ? 'gallery-modal__img--cta-layer' : ''}`"
              :uuid="uuid"
              :width="1000"
              :ratio="ratio(index)"
              :lazy="!isNextIndex(index)"
            />
          </div>

          <div class="gallery-modal__nav">
            <button
              class="gallery-modal__nav-button gallery-modal__nav-button--prev"
              :aria-label="trans('global.pagination.previous')"
              @click="previous"
            >
              <Icon
                :svg="IconCaretLeft"
                width="24"
                height="24"
                fill="black"
              />
            </button>

            <button
              class="gallery-modal__nav-button gallery-modal__nav-button--next"
              :aria-label="trans('global.pagination.next')"
              @click="next"
            >
              <Icon
                :svg="IconCaretRight"
                width="24"
                height="24"
                fill="black"
              />
            </button>
          </div>

          <div
            v-show="showCtaLayer"
            class="gallery-modal__ctas"
          >
            <Typography
              variant="content-heading-2"
              color="gray-darker"
            >
              {{ trans('offer.know_more.title') }}
            </Typography>

            <ContactOfferButton
              :hash-ids="contactHashIds"
              tracking-event="contact_offer_photos"
              :disabled="isOwner"
              :proposal-id="proposalId"
            >
              <Icon
                :svg="IconChatsFill"
                width="24"
                height="24"
                fill="white"
              />

              <span>
                {{ trans('offer.contact.actions.contact') }}
              </span>
            </ContactOfferButton>
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
@use '@styles/variables' as *;
@use '@styles/mixins' as *;

.gallery-modal {
  z-index: 150;
  display: flex;
  justify-content: start;
  align-items: flex-start;

  // @see gallery-modal.scss for modal-container class

  &__body-wrapper {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__body {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    max-width: 100%;
    max-height: calc(100% - $galleryHeaderHeight);
    overflow: hidden;
  }

  &__img {
    max-width: 100%;
    max-height: 100%;
  }

  &__pictures {
    max-width: 100%;
    max-height: 100%;
  }

  &__nav {
    position: absolute;
    width: 100%;
    justify-content: space-between;
    display: flex;
    padding: rem(8);
  }

  &__nav-button {
    display: block;
    width: rem(40);
    height: rem(40);
    border: 1px solid $colorGray;
    border-radius: rem(4);
    background-color: $colorWhite;
    z-index: 2;
    padding: rem(8);
  }

  &__ctas {
    position: absolute;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: rem(4);
    min-width: rem(160);
  }
}

@media screen and (min-width: $breakTablet) {
  .gallery-modal {
    &__ctas {
      gap: rem(8);
    }
  }
}
</style>
